import { isAfter, isSameDay } from 'date-fns';
import * as mobx from 'mobx';
import { IntlShape } from 'react-intl';
import * as IoC from 'typescript-ioc';
import { IEventDetailsDTO } from '../../../../../../../api/events.dao';
import { IScheduledEmailDAO, IScheduledEmailDTO, ScheduledTypes } from '../../../../../../../api/scheduled-email.dao';
import { IErrorParsing } from '../../../../../../../infrastructure/error-parsing';
import { IGlobalStorage } from '../../../../../../../infrastructure/global-storage';
import { utcToZonedTime } from '../../../../../../../infrastructure/utils/date-fns-overrides.utils';
import { alertServerError, showNotification } from '../../../../../../global/modal/alerts-host/alert-templates';
import { ScheduledEmailDataVM } from './scheduled-email-form-data.viewmodel';
import { NEW_SCHEDULED_EMAIL_ADDED_TEXT, SCHEDULED_EMAIL_UPDATED_TEXT } from './scheduled-email-form.texts';
import { ERROR_TEXT } from '../../../../../../../constants/common-labels.constants';
import { DateFnsFormats, IDateTimeFormatter, TimeFormats } from '../../../../../../../infrastructure/date-formatter';
import { TObject } from '../../../../../../../infrastructure/type-extensions';
import { IMeetingDetailsDTO } from '../../../../../../../api/meetings.dao';
import { AttendeeRole } from '../../../../../../../api/attendees.dao';
import { IReactIntlProvider } from '../../../../../../../infrastructure/react-intl-provider';
import { transpileClassesToInlineStyles, transpileInlineStylesToClasses } from '../../../../../../global/rich-text-editor/utils/transpiler';

export interface IMeetingData extends Partial<IMeetingDetailsDTO> {
    roles: AttendeeRole[];
}

class ScheduledEmailFormVM {
    @mobx.observable
    public isSaving: boolean;

    public formData: ScheduledEmailDataVM;

    @mobx.observable
    public event: IEventDetailsDTO;

    constructor(
        public accountId: number,
        public saveCompletionCallback: (data: IScheduledEmailDTO) => void,
        public updateCompletionCallback: (data: IScheduledEmailDTO) => void,
        public scheduledEmailDTO: IScheduledEmailDTO | undefined,
        public type: ScheduledTypes,
        public meetingData: IMeetingData,
        _event: IEventDetailsDTO,
        @IoC.Inject private readonly errorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject private readonly scheduledEmailDAO: IScheduledEmailDAO = IoC.Container.get(IScheduledEmailDAO),
        @IoC.Inject private readonly globalStorage: IGlobalStorage = IoC.Container.get(IGlobalStorage),
        @IoC.Inject private readonly dateTimeFormatter: IDateTimeFormatter = IoC.Container.get(IDateTimeFormatter),
        @IoC.Inject private readonly intlProvider: IReactIntlProvider = IoC.Container.get(IReactIntlProvider),
    ) {
        this.event = _event;

        mobx.reaction(() => this.globalStorage.getEvent(), () => {
            this.event = this.globalStorage.getEvent();
        });
    }

    public get intl(): IntlShape {
        return this.intlProvider.getIntl();
    }

    public get isEdit(): boolean {
        return this.scheduledEmailDTO && !!this.scheduledEmailDTO.id;
    }

    public initialize(): void {
        this.formData = new ScheduledEmailDataVM(this.event, this.scheduledEmailDTO, this.type, this.meetingData);
    }

    public startSaving = (): void => {
        this.isSaving = true;
    };

    public stopSaving = (): void => {
        this.isSaving = false;
    };

    public getEventDatesArray(): Date[] {
        const currentDateInEventTimezone = utcToZonedTime(new Date(), this.event.timeZone);

        const res = [];
        const startDate = new Date(this.event.startDateTime.getTime());
        const endDate = new Date(this.event.endDateTime.getTime());
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(0, 0, 0, 0);
        while (startDate <= endDate) {
            if (isAfter(startDate, currentDateInEventTimezone) || isSameDay(startDate, currentDateInEventTimezone)) {
                res.push(new Date(startDate.getTime()));
            }
            startDate.setDate(startDate.getDate() + 1);
        }
        return res;
    }

    @mobx.action
    public createScheduledEmail = async (): Promise<boolean> => {
        try {
            mobx.runInAction(this.startSaving);

            const data = this.formData.toCreateApiDTO();

            // In case we have body with Rich Text editor styles, we need to transpile them to classes
            if (data?.emailTemplate?.body) {
                data.emailTemplate.body = transpileClassesToInlineStyles(this.formData?.toCreateApiDTO()?.emailTemplate?.body);
            }

            const response = await this.scheduledEmailDAO.createScheduledEmail({
                accountId: this.accountId,
                eventId: this.event.eventId,
                ...this.getMeetingDataDTO(),
                ...data
            });

            showNotification(this.intl.formatMessage(NEW_SCHEDULED_EMAIL_ADDED_TEXT));

            if (response?.emailTemplate?.body) {
                response.emailTemplate.body = transpileInlineStylesToClasses(response.emailTemplate.body);
            }

            this.saveCompletionCallback(response);
            return true;
        } catch (e) {
            this.handleApiError(e);
            return false;
        } finally {
            mobx.runInAction(this.stopSaving);
        }
    };

    @mobx.action
    public editScheduledEmail = async (): Promise<boolean> => {
        try {
            mobx.runInAction(this.startSaving);
            const data = this.formData.toUpdateApiDTO();

            if (data?.emailTemplate?.body) {
                data.emailTemplate.body = transpileClassesToInlineStyles(this.formData?.toUpdateApiDTO()?.emailTemplate?.body);
            }

            const response = await this.scheduledEmailDAO.updateScheduledEmail({
                accountId: this.accountId,
                eventId: this.event.eventId,
                ...data
            });

            showNotification(this.intl.formatMessage(SCHEDULED_EMAIL_UPDATED_TEXT));

            if (response?.emailTemplate?.body) {
                response.emailTemplate.body = transpileInlineStylesToClasses(response.emailTemplate.body);
            }

            this.updateCompletionCallback(response);
            return true;
        } catch (e) {
            this.handleApiError(e);
            return false;
        } finally {
            mobx.runInAction(this.stopSaving);
        }
    };

    @mobx.action
    public performSaveCall = async (): Promise<boolean> => {
        if (!this.formData.checkForm()) return;

        return this.isEdit
            ? this.editScheduledEmail()
            : this.createScheduledEmail();
    };

    public getDateFormat = (): DateFnsFormats => {
        return this.dateTimeFormatter.getLocalTimeFormat() === TimeFormats.Day24Hours
            ? DateFnsFormats.StringifiedShortFullDateDay24Hours
            : DateFnsFormats.StringifiedShortFullDateDay12Hours;
    };

    public getDayStringInEventTimezone = (date: Date): string => {
        return this.dateTimeFormatter.dateToFormattedString(
            date,
            DateFnsFormats.StringifiedFullDayMonthNumericDateYear,
            this.event.timeZone
        );
    };

    public getFormmatedTime = (date: Date): string => {
        return this.dateTimeFormatter.dateToFormattedString(date, this.dateTimeFormatter.getLocalTimeFormat(), this.event.timeZone);
    };

    public handleSendPreview = async (): Promise<void> => {
        try {
            await this.scheduledEmailDAO.sendPreviewScheduledEmail({
                accountId: this.accountId,
                eventId: this.event.eventId,
                id: this.scheduledEmailDTO.id,
                meetingId: this.scheduledEmailDTO.meetingId
            });
            showNotification(this.intl.formatMessage({
                id: 'oe.websiteTab.previewSent',
                defaultMessage: 'Preview sent',
                description: 'Notification message',
            }));

        // eslint-disable-next-line no-empty
        } catch (e) {}
    };

    @mobx.action
    private handleApiError = (e): void => {
        alertServerError(this.intl.formatMessage(ERROR_TEXT), this.errorParsing.extractMessage(e));
    };

    private getMeetingDataDTO = (): IMeetingData | TObject => {
        if (!this.meetingData || !this.meetingData.meetingId) return {};
        return {
            meetingId: this.meetingData.meetingId,
            roles: this.meetingData.roles,
        };
    };
}

export { ScheduledEmailFormVM };
